/**
 * WebSocket Context
 *
 * Provides WebSocket connection state and methods to the entire app.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  WebSocketClient,
  getWebSocketClient,
  destroyWebSocketClient,
  type WebSocketEvent,
  type EventHandler,
  type EventType,
} from '@/lib/websocket';
import { decisionKeys } from '@/hooks/useDecisions';
import { analyticsKeys } from '@/hooks/useAnalytics';

// =============================================================================
// Types
// =============================================================================

interface WebSocketContextValue {
  isConnected: boolean;
  connectionId: string | null;
  subscribe: (channels: string | string[]) => void;
  unsubscribe: (channels: string | string[]) => void;
  on: <T = unknown>(eventType: EventType | '*', handler: EventHandler<T>) => () => void;
  send: (message: Record<string, unknown>) => boolean;
  lastEvent: WebSocketEvent | null;
  reconnect: () => void;
}

// =============================================================================
// Context
// =============================================================================

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface WebSocketProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function WebSocketProvider({
  children,
  enabled = true,
}: WebSocketProviderProps) {
  const { getAccessToken, isAuthenticated, isLoading } = useAuth();
  const queryClient = useQueryClient();

  const [client, setClient] = useState<WebSocketClient | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<WebSocketEvent | null>(null);

  // Initialize WebSocket client
  useEffect(() => {
    if (!enabled || isLoading) return;

    const initializeWebSocket = async () => {
      let token: string | undefined;

      if (isAuthenticated) {
        const accessToken = getAccessToken();
        if (accessToken) {
          token = accessToken;
        }
      }

      const wsClient = getWebSocketClient({
        token,
        onConnect: () => {
          setIsConnected(true);
        },
        onDisconnect: () => {
          setIsConnected(false);
          setConnectionId(null);
        },
        onError: (error) => {
          console.error('WebSocket error:', error);
        },
      });

      // Handle connection event to get connection ID
      wsClient.on('connected', (event) => {
        setConnectionId(event.data.connection_id as string);
      });

      // Set up cache invalidation handlers
      wsClient.on('decision.created', () => {
        queryClient.invalidateQueries({ queryKey: decisionKeys.all });
        queryClient.invalidateQueries({ queryKey: analyticsKeys.stats() });
      });

      wsClient.on('decision.approved', () => {
        queryClient.invalidateQueries({ queryKey: decisionKeys.all });
        queryClient.invalidateQueries({ queryKey: analyticsKeys.stats() });
      });

      wsClient.on('decision.rejected', () => {
        queryClient.invalidateQueries({ queryKey: decisionKeys.all });
        queryClient.invalidateQueries({ queryKey: analyticsKeys.stats() });
      });

      wsClient.on('capacity.updated', () => {
        queryClient.invalidateQueries({ queryKey: analyticsKeys.capacity() });
      });

      wsClient.on('agent.status_changed', () => {
        queryClient.invalidateQueries({ queryKey: analyticsKeys.agents() });
      });

      // Track all events
      wsClient.on('*', (event) => {
        setLastEvent(event);
      });

      wsClient.connect();
      setClient(wsClient);
    };

    initializeWebSocket();

    return () => {
      // Cleanup on unmount
      destroyWebSocketClient();
      setClient(null);
      setIsConnected(false);
      setConnectionId(null);
    };
  }, [enabled, isAuthenticated, isLoading, getAccessToken, queryClient]);

  // Subscribe to channels
  const subscribe = useCallback(
    (channels: string | string[]) => {
      client?.subscribe(channels);
    },
    [client]
  );

  // Unsubscribe from channels
  const unsubscribe = useCallback(
    (channels: string | string[]) => {
      client?.unsubscribe(channels);
    },
    [client]
  );

  // Add event handler
  const on = useCallback(
    <T = unknown,>(eventType: EventType | '*', handler: EventHandler<T>) => {
      if (!client) {
        return () => {};
      }
      return client.on(eventType, handler);
    },
    [client]
  );

  // Send message
  const send = useCallback(
    (message: Record<string, unknown>): boolean => {
      return client?.send(message) ?? false;
    },
    [client]
  );

  // Reconnect
  const reconnect = useCallback(() => {
    if (client) {
      client.disconnect();
      client.connect();
    }
  }, [client]);

  const value: WebSocketContextValue = {
    isConnected,
    connectionId,
    subscribe,
    unsubscribe,
    on,
    send,
    lastEvent,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }

  return context;
}

// =============================================================================
// Connection Status Component
// =============================================================================

interface ConnectionStatusProps {
  showWhenConnected?: boolean;
}

export function ConnectionStatus({ showWhenConnected = false }: ConnectionStatusProps) {
  const { isConnected } = useWebSocketContext();

  if (!showWhenConnected && isConnected) {
    return null;
  }

  return (
    <div
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
        ${isConnected
          ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
          : 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400'
        }
      `}
    >
      <span
        className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success-500 animate-pulse' : 'bg-warning-500'}`}
      />
      {isConnected ? 'Live' : 'Connecting...'}
    </div>
  );
}
